import { errorMsg } from '../../utils/errorMsg';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Box, Typography, Button, TextField, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import api from '../../axios';
import ModuleHeader from '../../components/ModuleHeader';

const TEAL = '#00F1A7';
const GREEN = '#10B981';
const RED = '#EF4444';

const tabSx = {
  mb: 2,
  '& .MuiTab-root': { color: 'text.secondary', fontWeight: 600, textTransform: 'none' },
  '& .Mui-selected': { color: 'text.primary' },
};

function formatCurrency(value) {
  if (value == null) return 'R$ 0,00';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num)) return 'R$ 0,00';
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function ContasFinanceiro({ inTab } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const tabPaths = ['/financeiro', '/financeiro/lancamentos', '/financeiro/categorias', '/financeiro/contas'];
  const tabIndex = Math.max(0, tabPaths.indexOf(location.pathname));

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const [contas, setContas] = useState([]);

  const [openModal, setOpenModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [formNome, setFormNome] = useState('');
  const [formBanco, setFormBanco] = useState('');
  const [formAgencia, setFormAgencia] = useState('');
  const [formContaNumero, setFormContaNumero] = useState('');
  const [formSaldoInicial, setFormSaldoInicial] = useState('');

  const [openImport, setOpenImport] = useState(false);
  const [importContaId, setImportContaId] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importFileLabel, setImportFileLabel] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);

  const carregarContas = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const res = await api.get('/financeiro/contas');
      setContas(res.data || []);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao carregar contas');
    }
    setLoading(false);
  }, []);

  useEffect(() => { carregarContas(); }, [carregarContas]);

  const resetForm = () => {
    setEditId(null);
    setFormNome('');
    setFormBanco('');
    setFormAgencia('');
    setFormContaNumero('');
    setFormSaldoInicial('');
    setErro('');
    setOpenModal(false);
  };

  const handleSubmit = async () => {
    setErro('');
    if (!formNome) { setErro('Preencha o nome da conta'); return; }
    try {
      const payload = {
        nome: formNome,
        banco: formBanco || null,
        agencia: formAgencia || null,
        conta_numero: formContaNumero || null,
        saldo_inicial: formSaldoInicial ? parseFloat(formSaldoInicial.replace(',', '.')) : 0,
      };
      if (editId) {
        await api.put(`/financeiro/contas/${editId}`, payload);
        setSucesso('Conta atualizada!');
      } else {
        await api.post('/financeiro/contas', payload);
        setSucesso('Conta criada!');
      }
      resetForm();
      carregarContas();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao salvar');
    }
  };

  const handleDelete = async (id) => {
    setErro('');
    try {
      await api.delete(`/financeiro/contas/${id}`);
      setSucesso('Conta excluída!');
      setConfirmDelete(null);
      carregarContas();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao excluir');
    }
  };

  const handleEdit = (conta) => {
    setEditId(conta.id);
    setFormNome(conta.nome || '');
    setFormBanco(conta.banco || '');
    setFormAgencia(conta.agencia || '');
    setFormContaNumero(conta.conta_numero || '');
    setFormSaldoInicial(String(conta.saldo_inicial || ''));
    setOpenModal(true);
  };

  const resetImport = () => {
    setOpenImport(false);
    setImportContaId('');
    setImportFile(null);
    setImportFileLabel('');
    setImportPreview(null);
    setImportLoading(false);
    setErro('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportFile(file);
    setImportFileLabel(file.name);
    setImportPreview(null);
  };

  const handleImport = async () => {
    setErro('');
    if (!importContaId) { setErro('Selecione uma conta'); return; }
    if (!importFile) { setErro('Selecione um arquivo'); return; }
    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('conta_id', importContaId);
      const res = await api.post('/financeiro/contas/importar-extrato', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportPreview(res.data);
      setSucesso('Extrato importado com sucesso!');
      carregarContas();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao importar extrato');
    }
    setImportLoading(false);
  };

  return (
    <>
      <Container maxWidth="lg" sx={inTab ? { px: 0 } : { py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
        {!inTab && <ModuleHeader module="Financeiro" title="Contas" />}

        {!inTab && (
          <Tabs
            value={tabIndex}
            onChange={(_, v) => navigate(tabPaths[v])}
            variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
            TabIndicatorProps={{ sx: { bgcolor: TEAL, height: 2, borderRadius: 2 } }}
            sx={tabSx}
          >
            <Tab label="Dashboard" />
            <Tab label="Lançamentos" />
            <Tab label="Categorias" />
            <Tab label="Contas" />
          </Tabs>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          {inTab && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountBalanceIcon sx={{ color: TEAL }} />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Contas Bancárias
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => setOpenImport(true)}
              sx={{ borderColor: TEAL, color: TEAL, '&:hover': { borderColor: TEAL, bgcolor: 'rgba(139,92,246,0.08)' } }}
            >
              Importar Extrato
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => { resetForm(); setOpenModal(true); }}
              sx={{ bgcolor: TEAL, color: '#fff', '&:hover': { filter: 'brightness(0.9)' } }}
            >
              Nova Conta
            </Button>
          </Box>
        </Box>

        {sucesso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>{sucesso}</Alert>}
        {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Banco</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Agência</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Conta</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Saldo Inicial</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contas.map((conta) => (
                  <TableRow key={conta.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{conta.nome}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{conta.banco || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{conta.agencia || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{conta.conta_numero || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: (conta.saldo_inicial || 0) >= 0 ? GREEN : RED }}
                      >
                        {formatCurrency(conta.saldo_inicial)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleEdit(conta)} sx={{ color: TEAL }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" color="error" onClick={() => setConfirmDelete(conta.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {contas.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Nenhuma conta cadastrada.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      <Dialog open={openModal} onClose={resetForm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, borderBottom: `2px solid ${TEAL}` }}>
          {editId ? 'Editar Conta' : 'Nova Conta'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Nome da Conta" value={formNome} onChange={e => setFormNome(e.target.value)} fullWidth size="small" />
            <TextField label="Banco" value={formBanco} onChange={e => setFormBanco(e.target.value)} fullWidth size="small" />
            <TextField label="Agência" value={formAgencia} onChange={e => setFormAgencia(e.target.value)} fullWidth size="small" />
            <TextField label="Número da Conta" value={formContaNumero} onChange={e => setFormContaNumero(e.target.value)} fullWidth size="small" />
            <TextField label="Saldo Inicial" value={formSaldoInicial} onChange={e => setFormSaldoInicial(e.target.value)} fullWidth size="small" placeholder="0,00" />
          </Box>
          {erro && <Alert severity="error" sx={{ mt: 2 }}>{erro}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={resetForm} startIcon={<CloseIcon />}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            sx={{ bgcolor: TEAL, color: '#fff', '&:hover': { filter: 'brightness(0.9)' } }}
          >
            {editId ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja excluir esta conta? Esta ação não pode ser desfeita.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(confirmDelete)}>Excluir</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openImport} onClose={resetImport} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, borderBottom: `2px solid ${TEAL}` }}>
          Importar Extrato
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Conta</InputLabel>
              <Select value={importContaId} label="Conta" onChange={e => setImportContaId(e.target.value)}>
                <MenuItem value="">Selecione uma conta</MenuItem>
                {contas.map(conta => (
                  <MenuItem key={conta.id} value={conta.id}>{conta.nome}{conta.banco ? ` - ${conta.banco}` : ''}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box>
              <Button variant="outlined" component="label" sx={{ borderColor: TEAL, color: TEAL, '&:hover': { borderColor: TEAL, bgcolor: 'rgba(139,92,246,0.08)' } }}>
                {importFileLabel || 'Selecionar Arquivo (CSV/Excel)'}
                <input type="file" accept=".csv,.xlsx,.xls" hidden onChange={handleFileChange} />
              </Button>
            </Box>
            {importLoading && <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>}
            {erro && <Alert severity="error">{erro}</Alert>}
            {importPreview && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Pré-visualização ({importPreview.total || importPreview.lancamentos?.length || 0} lançamentos)
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Valor</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Categoria</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(importPreview.lancamentos || []).slice(0, 20).map((lanc, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{lanc.data || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{lanc.descricao || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, color: (lanc.valor || 0) >= 0 ? GREEN : RED }}>
                            {formatCurrency(lanc.valor)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{lanc.categoria || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={resetImport} startIcon={<CloseIcon />}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            onClick={handleImport}
            disabled={importLoading || !importContaId || !importFile}
            sx={{ bgcolor: TEAL, color: '#fff', '&:hover': { filter: 'brightness(0.9)' } }}
          >
            Importar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
